/**
 * Los enlaces de un correo crudo.
 *
 * ── Por qué hace falta ────────────────────────────────────────────────────────
 * El manejador guarda el mensaje sin interpretar: analizar MIME arriesga el límite de CPU del
 * Worker, y un fallo ahí **pierde el correo** — que en este sistema suele ser una verificación
 * que no vuelve a llegar. Se eligió que se lea mal antes que perderse.
 *
 * Pero «se lee mal» resultó ser «no se puede usar». Estos correos vienen en quoted-printable,
 * que parte las líneas largas con un `=` al final y codifica los caracteres no ASCII como `=XX`.
 * Una URL de verificación queda cortada en dos, así que ni el buscador del navegador la
 * encuentra ni sirve copiarla a mano.
 *
 * Decodificar para MOSTRAR es distinto de decodificar para guardar: acá corre en el navegador,
 * con el mensaje ya a salvo, y si falla no se pierde nada.
 */

/**
 * Deshace quoted-printable: junta las líneas partidas y traduce los `=XX`.
 *
 * ── Por qué mira la cabecera, y qué pasó por no mirarla ───────────────────────
 * Decía que no hacía falta —«lo que se busca son URLs, que son ASCII»— y era falso. En un mensaje
 * que NO está codificado así, un `=` seguido de dos caracteres hexadecimales es texto normal, y
 * decodificarlo lo rompe:
 *
 *     https://fanvue.com/verify?token=abc123  →  https://fanvue.com/verify?token«c123
 *
 * `=ab` se leía como el byte 0xAB. Es exactamente el enlace que esta bandeja existe para mostrar,
 * corrompido en silencio y de una forma que parece un problema del remitente.
 *
 * Cuando el mensaje SÍ es quoted-printable el problema no existe: ahí un `=` literal viaja como
 * `=3D`, así que decodificar es lo correcto. Toda la diferencia está en preguntar antes.
 *
 * ── Y si la cabecera no aparece pero el cuerpo sí lo está ─────────────────────
 * Entonces las URLs quedan partidas en dos, que es el defecto que este archivo vino a arreglar.
 * Se elige ése a propósito: una URL cortada se ve cortada y se puede recomponer a mano; una
 * corrompida se ve entera, se pulsa, y falla sin decir por qué.
 */
function juntarLineasPartidas(crudo: string): string {
  const esQuotedPrintable = /^content-transfer-encoding:\s*quoted-printable/im.test(crudo);
  if (!esQuotedPrintable) return crudo;

  return crudo
    .replace(/=\r?\n/g, '')
    .replace(/=([0-9A-F]{2})/gi, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)));
}

/**
 * Los enlaces útiles del mensaje, el más probable primero.
 *
 * ── Por qué hay que filtrar tanto ─────────────────────────────────────────────
 * Un correo de plantilla trae el mismo puñado de enlaces repetido: la parte de texto y la de
 * HTML llevan los mismos, y en la de HTML los `&` vienen escritos como `&amp;`. Sin normalizar
 * eso, el mismo enlace aparece dos veces y parece que hay el doble.
 *
 * A eso se suman el logo de la cabecera, el pie de notificaciones y la URL del DTD del
 * documento. De ocho enlaces, el que se busca es uno.
 */
export function enlacesDe(crudo: string): string[] {
  const texto = juntarLineasPartidas(crudo);
  const encontrados = texto.match(/https?:\/\/[^\s"'<>)\]]+/g) ?? [];

  const limpios = encontrados
    .map((url) => url.replace(/&amp;/g, '&').replace(/[.,;:]+$/, ''))
    .filter((url) => !/\.(png|jpe?g|gif|webp|svg|css|woff2?|dtd)($|\?)/i.test(url))
    .filter((url) => !/\/(open|track|pixel|unsubscribe)\b/i.test(url))
    // El logo y el pie llevan su propósito en el `utm_content`, así que se reconocen sin
    // adivinar: son de plantilla y nunca son la acción del correo.
    .filter((url) => !/utm_content=(header_logo|manage_notifications)/i.test(url))
    .filter((url) => !/w3\.org/i.test(url));

  const unicos = [...new Set(limpios)];

  /*
    El que parece la acción, primero.

    Estos correos existen para que alguien pulse UN enlace —verificar, confirmar, restablecer— y
    el resto es decorado. Ordenarlos evita tener que leer ocho URLs de doscientos caracteres
    para encontrar la que tiene `verify` en el medio.
  */
  const esAccion = (url: string) =>
    /verify|confirm|activate|reset|password|invite|token|code=/i.test(url);

  return [...unicos.filter(esAccion), ...unicos.filter((url) => !esAccion(url))];
}

/**
 * El asunto, legible.
 *
 * ── Qué se ve sin esto ────────────────────────────────────────────────────────
 * `=?utf-8?q?Discover_Offers_&_Promotions_=F0=9F=A4=91?=`. Es RFC 2047: cuando un asunto tiene
 * caracteres fuera de ASCII —una tilde, un emoji— viaja codificado, y la bandeja lo mostraba tal
 * cual. En una lista de cuatro correos no se distingue cuál es cuál.
 *
 * ── Por qué acá y no al recibirlo ─────────────────────────────────────────────
 * Por lo mismo que el resto de este archivo: el manejador guarda el mensaje sin interpretar,
 * porque analizar MIME arriesga el límite de CPU del Worker y un fallo ahí PIERDE el correo — que
 * en este sistema suele ser una verificación que no vuelve a llegar. Decodificar para mostrar
 * corre en el navegador, con el mensaje ya a salvo, y si falla no se pierde nada.
 */
export function asuntoLegible(asunto: string): string {
  /*
    Dos palabras codificadas seguidas pierden el espacio que las separa.

    Lo dice el RFC: el espacio entre dos `encoded-word` es de la codificación, no del texto. Un
    asunto largo se parte en varias, y sin esto quedan separadas por espacios que nadie escribió.
  */
  const pegadas = asunto.replace(/\?=\s+=\?/g, '?==?');

  return pegadas.replace(
    /=\?([^?]+)\?([QqBb])\?([^?]*)\?=/g,
    (entero, juego: string, codificacion: string, texto: string) => {
      try {
        const bytes =
          codificacion.toUpperCase() === 'B'
            ? Uint8Array.from(atob(texto), (caracter) => caracter.charCodeAt(0))
            : bytesDeQ(texto);
        return new TextDecoder(juego.toLowerCase()).decode(bytes);
      } catch {
        /*
          Si no se puede, se devuelve lo que había.

          Un juego de caracteres que `TextDecoder` no conoce, o un base64 mal formado, no son
          motivo para dejar el asunto en blanco: ilegible se lee peor que crudo, pero vacío no se
          lee de ninguna forma.
        */
        return entero;
      }
    },
  );
}

/** El texto de un `encoded-word` en Q, que es quoted-printable con `_` por espacio. */
function bytesDeQ(texto: string): Uint8Array {
  const conEspacios = texto.replace(/_/g, ' ');
  const bytes: number[] = [];
  for (let i = 0; i < conEspacios.length; i += 1) {
    if (conEspacios[i] === '=' && /^[0-9A-F]{2}$/i.test(conEspacios.slice(i + 1, i + 3))) {
      bytes.push(parseInt(conEspacios.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      bytes.push(conEspacios.charCodeAt(i));
    }
  }
  return new Uint8Array(bytes);
}

/**
 * Si el mensaje es publicidad y no un trámite.
 *
 * ── Por qué hace falta separarlos ─────────────────────────────────────────────
 * Porque esta casilla existe para UNA cosa: el enlace de confirmación de un alta. Apenas la
 * dirección queda registrada en algún lado, la plataforma empieza a mandar campañas —cuatro
 * mensajes de Fanvue en dos días, con quince URLs de seguimiento cada uno— y el correo que
 * importa queda enterrado abajo.
 *
 * ── Cómo se reconoce, sin adivinar por el asunto ──────────────────────────────
 * Por `List-Unsubscribe`, que es la cabecera que el RFC 2369 reserva para envíos masivos. Los
 * correos de trámite no la llevan: no tiene sentido darse de baja de una verificación.
 *
 * Se mira también el remitente, porque las plataformas mandan sus campañas desde el dominio de
 * su proveedor de envíos —`sender-sib.com`, `sendgrid.net`— y sus trámites desde el propio.
 *
 * ── Por qué esto no ESCONDE nada ──────────────────────────────────────────────
 * Porque la clasificación puede equivocarse y el costo de equivocarse es alto: un enlace de
 * verificación que no aparece deja un alta trabada sin explicación. Los promocionales se
 * agrupan y se pliegan, y siguen estando a un clic con su asunto legible.
 */
export function esPromocional(correo: { remitente: string; cuerpo: string }): boolean {
  // Sólo la cabecera: el cuerpo de una campaña menciona «unsubscribe» en el pie, y eso no la
  // define — un trámite podría nombrarlo igual.
  const cabeceras = correo.cuerpo.split(/\r?\n\r?\n/)[0] ?? '';
  if (/^list-unsubscribe:/im.test(cabeceras)) return true;
  if (/^precedence:\s*(bulk|list|junk)/im.test(cabeceras)) return true;

  return /(sender-sib|sendibt\d*|sendgrid\.net|mailchimp|mailgun|brevo)\.com?\b/i.test(
    correo.remitente,
  );
}

/**
 * Los enlaces que se muestran, que no son todos.
 *
 * ── Por qué acotarlos ─────────────────────────────────────────────────────────
 * Porque `enlacesDe` devuelve todo lo que encuentra ordenado por probabilidad, y eso alcanzaba
 * cuando los correos eran verificaciones de ocho enlaces. Una campaña trae quince URLs de
 * seguimiento de trescientos caracteres, y volcarlas hace ilegible la bandeja entera — que es
 * exactamente lo que pasó.
 *
 * ── La regla ──────────────────────────────────────────────────────────────────
 * Si alguno parece la acción del correo, se muestran ésos y nada más: es lo que se vino a
 * buscar. Si ninguno lo parece, se muestran los tres primeros — no cero, porque el detector de
 * acciones es una heurística y un enlace de confirmación con una forma que no previmos tiene que
 * seguir estando a la vista.
 */
export function enlacesVisiblesDe(crudo: string): string[] {
  const todos = enlacesDe(crudo);
  const acciones = todos.filter((url) =>
    /verify|confirm|activate|reset|password|invite|token|code=/i.test(url),
  );
  return acciones.length > 0 ? acciones : todos.slice(0, 3);
}
